/**
 * Themed button built on the `.btn-*` component classes. Supports a loading
 * state (shows a spinner + disables) and three visual variants.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactElement } from 'react';
import Spinner from '@/components/common/Spinner';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
  loading?: boolean;
}

const variantClass: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
};

/** Button with variants + loading state. `type` defaults to "button". */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading = false, disabled, children, className = '', type = 'button', ...rest },
  ref,
): ReactElement {
  return (
    <button
      ref={ref}
      type={type}
      className={`${variantClass[variant]} ${className}`.trim()}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  );
});

export default Button;
